import type { GridFilterModel } from '@mui/x-data-grid-pro';
import { matchesFilter } from 'components/common/dataGrid/filters';
import type { DocumentState } from 'components/common/dataGrid/columnsFactory/types';
import type { DocumentationValueGetters } from 'components/common/dataGrid/columnsFactory/valueGetters';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { debugLog } from 'shared/logger/debugLog';
import { DirectoryItem, DirectoryStructure } from 'shared/types/documentation';
import { DocumentationViewItem } from 'store/documentation/model/types';
import { filterDocumentationsByState } from 'store/documentation/slices/filters/model';
import {
  DownloadItemsConfig,
  GetDirectories,
  GetDocumentations,
} from './types';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';

export function ensureFileNameExtension(fileName: string, extension?: string): string {
  if (!extension) {
    return fileName;
  }
  if (fileName.endsWith(`.${extension}`)) {
    return fileName;
  }
  return `${fileName}.${extension}`;
}

export function getLastVersionExtension(documentation: DocumentationModel): string {
  const lastVersion = documentation.versions?.[documentation.versions.length - 1];
  return lastVersion?.extension || '';
}

export async function getDirectoryStructure(
  directoryId: string,
  getDirectories: GetDirectories,
  getDocumentations: GetDocumentations
): Promise<DirectoryStructure> {
  const directories = await getDirectories(directoryId);
  const files = await getDocumentations(directoryId);

  return { directories, files };
}

export function applyFiltersToDirectoryStructure(
  directories: DirectoryModel[],
  files: DocumentationModel[],
  filterModel: GridFilterModel | undefined,
  valueGetters: DocumentationValueGetters,
  timezone: string,
  documentState?: DocumentState
): { directories: DirectoryModel[]; files: DocumentationModel[] } {
  let filteredFiles = documentState
    ? filterDocumentationsByState(files, documentState)
    : files;

  let filteredDirectories = directories;

  if (filterModel?.items?.length && valueGetters) {
    filteredDirectories = directories.filter((directory) =>
      matchesFilter(directory, filterModel, valueGetters, timezone)
    );

    filteredFiles = filteredFiles.filter((file) =>
      matchesFilter(file, filterModel, valueGetters, timezone)
    );
  }

  return { directories: filteredDirectories, files: filteredFiles };
}

export async function getDirectoryItems(
  directoryId: string,
  directoryName: string,
  config: DownloadItemsConfig,
  getDocumentationUrl: (documentation: DocumentationModel) => Promise<string | null>
): Promise<DirectoryItem[]> {
  const {
    getDirectories,
    getDocumentations,
    valueGetters,
    filterModel,
    timezone,
    documentState,
  } = config;
  const items: DirectoryItem[] = [];

  try {
    const { directories, files } = await getDirectoryStructure(
      directoryId,
      getDirectories,
      getDocumentations
    );

    const { directories: filteredDirectories, files: filteredFiles } =
      applyFiltersToDirectoryStructure(
        directories,
        files,
        filterModel,
        valueGetters,
        timezone,
        documentState
      );

    for (const file of filteredFiles) {
      if (!file.versions || file.versions.length === 0) {
        debugLog(`Skipping file ${file.name} - no versions available`);
        continue;
      }

      const url = await getDocumentationUrl(file);
      if (!url) {
        debugLog(`Skipping file ${file.name} - no URL available`);
        continue;
      }

      const extension = getLastVersionExtension(file);
      const fileName = ensureFileNameExtension(file.name, extension);

      items.push({
        name: fileName,
        path: `${directoryName}/${fileName}`,
        url,
      });
    }

    for (const directory of filteredDirectories) {
      if (!directory._id) {
        continue;
      }

      const subdirectoryItems = await getDirectoryItems(
        directory._id,
        `${directoryName}/${directory.name}`,
        config,
        getDocumentationUrl
      );

      items.push(...subdirectoryItems);
    }

    return items;
  } catch (error) {
    throw new Error(
      `Unable to get directory items for ${directoryId}: ${error}`
    );
  }
}
